import React, { useState, useEffect, useCallback } from 'react';

const CAFBC5Presentation = ({ onComplete, currentSlide: externalSlide, onSlideChange }) => {
  const [currentSlide, setCurrentSlide] = useState(externalSlide || 0);

  useEffect(() => {
    if (externalSlide !== undefined) setCurrentSlide(externalSlide);
  }, [externalSlide]);

  const bgGradient = 'linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 30%, #FECDD3 70%, #FDA4AF 100%)';
  const primaryColor = '#DC2626';
  const secondaryColor = '#B91C1C';
  const accentColor = '#EF4444';

  const slides = [
  // ============================================================
  // SLIDE 0 — TITRE
  // ============================================================
  { type: 'title', title: "Bloc 5", subtitle: "Formation transversale et complémentaire", chapters: "4 chapitres • Éthique • Droit • Numérique • Développement personnel", icon: "BC5" },

  // ============================================================
  // SLIDE 1 — PRÉSENTATION
  // ============================================================
  { type: 'content', title: "Présentation du Bloc 5",
    body: "Cette formation transversale vise à renforcer les compétences des responsables d'unité dans les dimensions éthiques, juridiques, numériques et personnelles du management dans le secteur social et médico-social.\n\nElle permet aux cadres d'analyser leur posture professionnelle, de comprendre les évolutions institutionnelles et d'adapter leurs pratiques aux transformations du secteur.",
    highlight: "Ce bloc mobilise des compétences fondamentales : réflexion éthique, maîtrise du cadre juridique, appropriation du numérique et développement de la posture managériale." },

  // ============================================================
  // SLIDE 2 — SOMMAIRE
  // ============================================================
  { type: 'sommaire', title: "Sommaire du Bloc 5", chapters: [
      { num: "1", title: "Chapitre 1 — Éthique et déontologie", targetSlide: 4 },
      { num: "2", title: "Chapitre 2 — Cadre juridique et institutionnel", targetSlide: 25 },
      { num: "3", title: "Chapitre 3 — Numérique et innovation", targetSlide: 43 },
      { num: "4", title: "Chapitre 4 — Développement personnel du cadre", targetSlide: 65 }
  ]},

  // ============================================================
  // SLIDE 3 — ENJEUX
  // ============================================================
  { type: 'content', title: "Les enjeux de la formation transversale",
    body: "Le secteur social et médico-social connaît des transformations profondes qui imposent aux cadres de développer des compétences au-delà de leur périmètre opérationnel.\n\nCes compétences transversales permettent de :",
    bullets: [
      "Ancrer les pratiques managériales dans un cadre éthique solide",
      "Sécuriser les décisions par la maîtrise du cadre juridique",
      "Accompagner la transformation numérique tout en préservant la relation humaine",
      "Développer une posture managériale équilibrée et prévenir l'épuisement professionnel"
    ],
    highlight: "Ces quatre dimensions sont complémentaires et constituent le socle d'un management responsable et durable dans le secteur social." },

  // ============================================================
  // CHAPITRE 1 — ÉTHIQUE ET DÉONTOLOGIE
  // ============================================================
  // SLIDE 4
  { type: 'section', title: "Chapitre 1", subtitle: "Éthique et déontologie", description: "Fondements éthiques du travail social, dilemmes éthiques, secret professionnel et secret partagé." },

  // SLIDE 5
  { type: 'content', title: "Introduction — Chapitre 1",
    body: "Les professionnels du secteur social et médico-social interviennent dans des situations humaines complexes, souvent marquées par la vulnérabilité, la précarité ou la dépendance.\n\nDans ce contexte, les décisions professionnelles ne peuvent pas reposer uniquement sur des règles juridiques ou administratives. Elles nécessitent une réflexion éthique permettant de prendre en compte les droits des personnes, les valeurs du travail social et les conséquences des décisions prises.\n\nLe responsable d'unité est particulièrement concerné par cette dimension. En tant que cadre, il doit soutenir les professionnels dans l'analyse des situations complexes et favoriser une culture éthique au sein de l'équipe." },

  // SLIDE 6
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 1",
    body: "À l'issue de ce module, les participants seront capables de :",
    bullets: [
      "Comprendre les fondements éthiques du travail social",
      "Distinguer éthique, morale et déontologie",
      "Analyser les dilemmes éthiques rencontrés dans les situations professionnelles",
      "Articuler éthique, droit et responsabilité managériale",
      "Maîtriser les principes du secret professionnel et du secret partagé"
    ] },

  // SLIDE 7
  { type: 'grid', title: "Sommaire — Chapitre 1 : Éthique et déontologie", items: [
    { title: "1. Éthique, morale et déontologie", desc: "Définitions, distinctions et implications pour les pratiques professionnelles" },
    { title: "2. Les valeurs du travail social", desc: "Dignité, solidarité, justice sociale, autonomie et pouvoir d'agir" },
    { title: "3. La réflexion éthique en management", desc: "Dilemmes éthiques, rôle du cadre, démarche d'analyse éthique" },
    { title: "4. Secret professionnel et partagé", desc: "Fondements juridiques, conditions du partage, équilibre confidentialité/accompagnement" }
  ]},

  // SLIDE 8
  { type: 'section', title: "1. Éthique, morale et déontologie", subtitle: "Fondements et distinctions", description: "Trois notions fondamentales pour comprendre les enjeux de la pratique professionnelle." },

  // SLIDE 9
  { type: 'content', title: "Définition de l'éthique",
    body: "L'éthique peut être définie comme une réflexion critique sur les valeurs et les principes qui orientent l'action humaine.\n\nContrairement à une règle juridique, l'éthique ne fournit pas toujours une réponse unique. Elle invite à analyser les situations afin de déterminer la décision la plus juste dans un contexte donné.\n\nDans le travail social, l'éthique consiste à s'interroger sur la manière d'agir dans l'intérêt de la personne accompagnée tout en respectant les valeurs fondamentales du secteur.",
    highlight: "L'éthique est une démarche de questionnement permanent, pas un ensemble de réponses toutes faites." },

  // SLIDE 10
  { type: 'grid', title: "Morale, déontologie et éthique : trois notions distinctes", items: [
    { title: "La morale", desc: "Ensemble de normes et de valeurs considérées comme bonnes ou mauvaises dans une société. Renvoie à des principes généraux de comportement, souvent hérités de la culture ou de la religion." },
    { title: "La déontologie", desc: "Ensemble des règles et obligations qui encadrent l'exercice d'une profession. Inscrite dans des codes professionnels, règlements institutionnels et textes législatifs." },
    { title: "L'éthique", desc: "Se distingue par son caractère réflexif. Consiste à analyser les situations lorsque les règles existantes ne permettent pas de répondre clairement. Implique délibération et argumentation." }
  ]},

  // SLIDE 11
  { type: 'section', title: "2. Les valeurs du travail social", subtitle: "Cadre de référence des pratiques", description: "Les principes fondamentaux affirmés par la loi du 2 janvier 2002." },

  // SLIDE 12
  { type: 'content', title: "Les valeurs fondamentales du travail social",
    body: "Le travail social repose sur un ensemble de valeurs fondamentales qui orientent les pratiques professionnelles et constituent un cadre de référence commun.\n\nCes principes sont notamment affirmés dans la loi du 2 janvier 2002 rénovant l'action sociale et médico-sociale :",
    bullets: [
      "Le respect de la dignité humaine : valeur intrinsèque de chaque personne",
      "La solidarité : engagement collectif envers les plus vulnérables",
      "La justice sociale : égalité d'accès aux droits et aux services",
      "L'autonomie des personnes : capacité de décision et de choix",
      "La participation des usagers : implication dans les décisions qui les concernent"
    ] },

  // SLIDE 13
  { type: 'content', title: "La dignité de la personne",
    body: "La dignité peut être définie comme la valeur intrinsèque de chaque être humain qui impose le respect de sa personne indépendamment de sa situation sociale, de son état de santé ou de ses capacités.\n\nElle constitue un principe fondamental et implique :",
    bullets: [
      "Le respect de l'intégrité physique et morale des personnes",
      "La reconnaissance de leur identité et de leur singularité",
      "La prise en compte de leurs choix et de leur volonté",
      "La préservation de leur confidentialité et de leur intimité"
    ],
    highlight: "Dans l'accompagnement social, respecter la dignité signifie considérer la personne comme un sujet et non comme un objet de prise en charge." },

  // SLIDE 14
  { type: 'content', title: "L'autonomie et le pouvoir d'agir (empowerment)",
    body: "L'autonomie correspond à la capacité d'une personne à prendre des décisions concernant sa propre vie. L'objectif de l'accompagnement n'est pas seulement de répondre aux besoins immédiats mais de renforcer la capacité d'action des personnes.\n\nLe concept d'empowerment (pouvoir d'agir) désigne la capacité des individus ou des groupes à prendre le contrôle de leur vie et à participer activement aux décisions qui les concernent.",
    bullets: [
      "Soutenir l'expression et la parole des personnes accompagnées",
      "Favoriser leur participation aux décisions qui les concernent",
      "Renforcer leur autonomie et leurs compétences",
      "Accompagner sans se substituer : aider la personne à construire ses propres solutions"
    ] },

  // SLIDE 15
  { type: 'section', title: "3. La réflexion éthique", subtitle: "en situation de management", description: "Dilemmes éthiques, rôle du cadre et démarche d'analyse structurée." },

  // SLIDE 16
  { type: 'content', title: "Les dilemmes éthiques",
    body: "Un dilemme éthique correspond à une situation dans laquelle plusieurs valeurs ou obligations entrent en contradiction, rendant difficile le choix d'une décision.\n\nLes cadres du secteur social sont régulièrement confrontés à ces tensions. Exemples :",
    bullets: [
      "Respecter l'autonomie d'une personne OU assurer sa protection (mise en danger)",
      "Respecter la confidentialité OU protéger un tiers en danger (signalement)",
      "Respecter le choix d'un usager OU appliquer les règles institutionnelles",
      "Soutenir un professionnel en difficulté OU garantir la qualité du service"
    ],
    highlight: "Dans ces situations, le professionnel doit analyser les enjeux et prendre une décision argumentée, en s'appuyant sur une démarche d'analyse éthique." },

  // SLIDE 17
  { type: 'content', title: "Le rôle du cadre dans la réflexion éthique",
    body: "Le responsable d'unité joue un rôle essentiel dans l'accompagnement des équipes face aux dilemmes éthiques. Il ne s'agit pas de fournir des réponses toutes faites mais de créer les conditions d'une réflexion collective.",
    bullets: [
      "Favoriser les échanges entre professionnels sur les situations complexes",
      "Organiser des espaces de réflexion collective (comités d'éthique, groupes d'analyse)",
      "Soutenir l'analyse des situations en mobilisant les cadres de référence",
      "Développer une culture éthique au sein de l'équipe",
      "Assumer la responsabilité de la décision finale tout en l'argumentant"
    ] },

  // SLIDE 18
  { type: 'grid', title: "La démarche d'analyse éthique en 5 étapes", items: [
    { title: "1. Identifier la situation", desc: "Décrire précisément la situation problématique, les acteurs impliqués et les difficultés rencontrées." },
    { title: "2. Repérer les valeurs en jeu", desc: "Identifier les valeurs ou principes qui entrent en tension (dignité, protection, autonomie, etc.)." },
    { title: "3. Analyser les conséquences", desc: "Évaluer les effets possibles des différentes décisions sur la personne, l'équipe et l'institution." },
    { title: "4. Délibérer collectivement", desc: "Croiser les points de vue des professionnels pour enrichir l'analyse et éviter les biais individuels." },
    { title: "5. Décider et argumenter", desc: "Prendre une décision justifiée par l'analyse des enjeux. Tracer la décision et ses motivations." }
  ]},

  // SLIDE 19
  { type: 'section', title: "4. Le secret professionnel", subtitle: "et le secret partagé", description: "Obligations légales, conditions du partage et équilibre confidentialité/accompagnement." },

  // SLIDE 20
  { type: 'content', title: "Le secret professionnel",
    body: "Le secret professionnel correspond à l'obligation pour certains professionnels de ne pas divulguer les informations confidentielles dont ils ont connaissance dans l'exercice de leurs fonctions.\n\nCette obligation vise à protéger la vie privée des personnes accompagnées et à instaurer une relation de confiance.",
    bullets: [
      "Fondement juridique : article 226-13 du Code pénal",
      "La violation du secret professionnel peut entraîner des sanctions pénales",
      "Concerne toutes les informations recueillies dans le cadre professionnel",
      "S'applique même après la fin de la prise en charge"
    ] },

  // SLIDE 21
  { type: 'content', title: "Le secret partagé",
    body: "Dans certaines situations, les professionnels doivent échanger des informations afin d'assurer la continuité de l'accompagnement. Le secret partagé permet cet échange entre professionnels intervenant auprès d'une même personne.\n\nCependant, ce partage est strictement encadré. Conditions :",
    bullets: [
      "Être justifié par l'intérêt de la personne accompagnée",
      "Être limité aux informations strictement nécessaires à l'accompagnement",
      "Être partagé uniquement entre professionnels directement concernés",
      "Respecter le consentement de la personne (sauf exceptions légales)"
    ],
    highlight: "Le respect de ces règles permet de concilier protection de la vie privée et qualité de l'accompagnement pluridisciplinaire." },

  // SLIDE 22
  { type: 'content', title: "Conclusion — Chapitre 1",
    body: "L'éthique et la déontologie constituent des dimensions fondamentales du travail social et du management en secteur social et médico-social.\n\nPour le responsable d'unité, elles impliquent :",
    bullets: [
      "Une capacité d'analyse des situations complexes et des tensions de valeurs",
      "Une attention constante au respect des droits et de la dignité des personnes",
      "Un soutien actif aux équipes dans la réflexion éthique collective",
      "La maîtrise des règles du secret professionnel et du secret partagé"
    ],
    highlight: "En développant une culture éthique, le cadre améliore la qualité de l'accompagnement et renforce la confiance des usagers." },

  // ============================================================
  // CHAPITRE 2 — CADRE JURIDIQUE ET INSTITUTIONNEL
  // ============================================================
  // SLIDE 23 — padding slides for alignment
  { type: 'content', title: "Transition vers le Chapitre 2",
    body: "Le cadre juridique constitue le socle sur lequel reposent les pratiques professionnelles du secteur social et médico-social. La connaissance des textes fondateurs et de l'organisation administrative est indispensable pour le responsable d'unité.",
    highlight: "Chapitre suivant : les principales lois qui structurent le secteur, l'organisation administrative française et l'impact des réformes sur les pratiques." },

  // SLIDE 24 — extra content slide
  { type: 'content', title: "L'articulation entre éthique et droit",
    body: "L'éthique et le droit sont complémentaires mais distincts. Le droit fixe un cadre obligatoire minimal ; l'éthique invite à aller au-delà en questionnant le sens des pratiques.",
    bullets: [
      "Le droit définit ce qui est permis et interdit (cadre contraignant)",
      "L'éthique interroge ce qui est juste et souhaitable (cadre réflexif)",
      "En situation de dilemme, le cadre juridique constitue un repère mais ne suffit pas toujours",
      "La réflexion éthique complète le droit en prenant en compte la singularité de chaque situation"
    ] },

  // SLIDE 25
  { type: 'section', title: "Chapitre 2", subtitle: "Cadre juridique et institutionnel", description: "Les textes fondateurs, l'organisation administrative et l'impact des réformes sur les pratiques." },

  // SLIDE 26
  { type: 'content', title: "Introduction — Chapitre 2",
    body: "Le secteur social et médico-social est fortement encadré par un ensemble de textes législatifs et réglementaires qui définissent les droits des usagers, les missions des établissements, les modalités de financement et les obligations des professionnels.\n\nCes textes visent à garantir la qualité des accompagnements, le respect des droits des personnes et l'égalité d'accès aux services.\n\nPour les cadres, la maîtrise du cadre juridique constitue une compétence essentielle. Elle permet de sécuriser les pratiques et de garantir la conformité des actions." },

  // SLIDE 27
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 2",
    body: "À l'issue de ce module, les participants seront capables de :",
    bullets: [
      "Comprendre les principaux textes législatifs du secteur social et médico-social",
      "Analyser les évolutions des politiques publiques dans le champ de l'action sociale",
      "Situer leur établissement dans l'organisation administrative française",
      "Comprendre les rôles des différentes autorités de régulation et de financement",
      "Identifier l'impact des réformes sur les pratiques et le management"
    ] },

  // SLIDE 28
  { type: 'grid', title: "Sommaire — Chapitre 2 : Cadre juridique", items: [
    { title: "1. Loi 2002-2", desc: "Rénovation de l'action sociale, droits des usagers, outils obligatoires" },
    { title: "2. Loi du 11 février 2005", desc: "Égalité des droits et des chances, définition du handicap, accessibilité" },
    { title: "3. Loi ASV (2015)", desc: "Adaptation au vieillissement, maintien à domicile, soutien aux aidants" },
    { title: "4. Loi HPST et organisation", desc: "Création des ARS, organisation administrative, rôle des départements" }
  ]},

  // SLIDE 29
  { type: 'section', title: "1. La loi du 2 janvier 2002", subtitle: "Rénovant l'action sociale et médico-sociale", description: "Texte fondateur qui place l'usager au centre des dispositifs d'accompagnement." },

  // SLIDE 30
  { type: 'content', title: "Contexte et principes de la loi 2002-2",
    body: "La loi n°2002-2 du 2 janvier 2002 constitue un texte fondateur dans l'évolution du secteur. Elle intervient dans un contexte marqué par la volonté d'améliorer la qualité des accompagnements et de renforcer les droits des usagers.\n\nPrincipes majeurs :",
    bullets: [
      "Reconnaissance des droits des usagers : sujets de droit, pas simples bénéficiaires",
      "Respect de la dignité et de l'intégrité des personnes",
      "Confidentialité des informations personnelles",
      "Participation des usagers aux décisions qui les concernent",
      "Amélioration continue de la qualité des services"
    ] },

  // SLIDE 31
  { type: 'grid', title: "Les outils introduits par la loi 2002-2", items: [
    { title: "Projet d'établissement", desc: "Document stratégique définissant les valeurs, orientations et objectifs. Durée de 5 ans. Cadre de référence pour tous les professionnels." },
    { title: "Livret d'accueil", desc: "Remis à chaque personne accueillie. Présente les missions, les droits des usagers et les modalités d'accompagnement." },
    { title: "Contrat de séjour", desc: "Formalise les conditions de l'accompagnement : prestations, droits et obligations, participation financière." },
    { title: "Conseil de la Vie Sociale", desc: "Instance de participation des usagers et familles. Expression sur l'organisation, la qualité et les projets." }
  ]},

  // SLIDE 32
  { type: 'content', title: "Impact de la loi 2002-2 sur les pratiques",
    body: "Cette loi a profondément transformé les pratiques du secteur social et médico-social. Elle a initié un changement culturel majeur.",
    bullets: [
      "Renforcement de la participation effective des usagers (au-delà du discours)",
      "Formalisation des pratiques (protocoles, procédures, évaluations)",
      "Développement de la culture de l'évaluation et de la qualité",
      "Transformation de la relation professionnel-usager (partenariat vs assistance)",
      "Obligation de transparence et de redevabilité des établissements"
    ],
    highlight: "Les professionnels doivent désormais inscrire leur action dans une démarche de qualité permanente et de respect effectif des droits des personnes." },

  // SLIDE 33
  { type: 'section', title: "2. Loi du 11 février 2005", subtitle: "Pour l'égalité des droits et des chances", description: "Texte majeur de la politique du handicap vers une société plus inclusive." },

  // SLIDE 34
  { type: 'content', title: "La loi du 11 février 2005",
    body: "La loi n°2005-102 du 11 février 2005 constitue un texte majeur dans la politique du handicap en France. Elle vise à garantir l'égalité des droits et des chances des personnes en situation de handicap.\n\nDéfinition élargie du handicap : toute limitation d'activité ou restriction de participation à la vie en société subie par une personne en raison d'une altération durable d'une fonction physique, sensorielle, mentale ou psychique.",
    highlight: "Cette définition prend en compte l'interaction entre les limitations de la personne et son environnement (approche sociale du handicap)." },

  // SLIDE 35
  { type: 'grid', title: "Principes fondamentaux de la loi 2005", items: [
    { title: "Droit à la compensation", desc: "Mesures visant à compenser les conséquences du handicap : aides humaines, techniques et financières (PCH)." },
    { title: "Accessibilité universelle", desc: "Obligation d'accessibilité des espaces publics, des transports et des ERP pour tous les citoyens." },
    { title: "Inclusion scolaire", desc: "Scolarisation des enfants handicapés en milieu ordinaire. Participation à la vie sociale et professionnelle." },
    { title: "MDPH", desc: "Création des Maisons Départementales des Personnes Handicapées : guichet unique d'accueil et d'orientation." }
  ]},

  // SLIDE 36
  { type: 'section', title: "3. Loi ASV (2015)", subtitle: "Adaptation de la société au vieillissement", description: "Réponse aux enjeux du vieillissement démographique." },

  // SLIDE 37
  { type: 'content', title: "La loi d'adaptation de la société au vieillissement",
    body: "La loi du 28 décembre 2015 (loi ASV) vise à répondre aux enjeux liés au vieillissement de la population. L'allongement de l'espérance de vie entraîne une augmentation du nombre de personnes âgées en perte d'autonomie.\n\nPrincipaux axes :",
    bullets: [
      "Renforcement de l'accompagnement à domicile (amélioration des services d'aide)",
      "Soutien aux proches aidants (reconnaissance de leur rôle, solutions de répit)",
      "Amélioration de la qualité dans les établissements pour personnes âgées",
      "Prévention de la perte d'autonomie (actions collectives, adaptation du logement)",
      "Revalorisation de l'APA à domicile"
    ] },

  // SLIDE 38
  { type: 'section', title: "4. Loi HPST et organisation administrative", subtitle: "Modernisation du système de santé", description: "Coordination sanitaire-social et rôle des autorités de régulation." },

  // SLIDE 39
  { type: 'content', title: "La loi HPST (2009) et les ARS",
    body: "La loi HPST (Hôpital, Patients, Santé, Territoires) adoptée en 2009 vise à moderniser le système de santé français en renforçant la coordination entre les secteurs sanitaire, social et médico-social.\n\nPrincipale innovation : la création des Agences Régionales de Santé (ARS).\n\nMissions des ARS :",
    bullets: [
      "Planification de l'offre de soins et de services à l'échelle régionale",
      "Régulation du secteur médico-social (autorisations, contrôles)",
      "Financement de certains établissements et services",
      "Coordination des parcours de santé sur le territoire"
    ] },

  // SLIDE 40
  { type: 'grid', title: "L'organisation administrative française", items: [
    { title: "L'État", desc: "Définit les grandes orientations des politiques sociales : lois, décrets, politiques nationales de santé et d'action sociale." },
    { title: "Les ARS", desc: "Pilotent les politiques sanitaires et médico-sociales au niveau régional : régulation de l'offre, planification, allocation des ressources." },
    { title: "Les Conseils départementaux", desc: "Rôle central : aide sociale à l'enfance (ASE), accompagnement des personnes âgées et handicapées, financement des établissements." },
    { title: "Les communes et intercommunalités", desc: "Actions de proximité : CCAS, prévention, logement social, coordination locale des politiques sociales." }
  ]},

  // SLIDE 41
  { type: 'content', title: "Conclusion — Chapitre 2",
    body: "La compréhension du cadre juridique et institutionnel constitue un élément essentiel pour les cadres du secteur social et médico-social.\n\nElle permet :",
    bullets: [
      "De sécuriser les pratiques professionnelles et les décisions managériales",
      "De garantir le respect effectif des droits des usagers",
      "D'inscrire l'action de l'établissement dans les politiques publiques",
      "D'anticiper les impacts des réformes sur l'organisation et les pratiques"
    ],
    highlight: "Le responsable d'unité doit être capable de comprendre les évolutions législatives et d'en analyser les impacts concrets sur son service." },

  // SLIDE 42 — Transition
  { type: 'content', title: "Des lois aux pratiques numériques",
    body: "Le cadre juridique encadre désormais aussi la transformation numérique du secteur (RGPD, dossier usager informatisé, télémédecine). Le chapitre suivant explore ces enjeux.",
    highlight: "Le numérique transforme profondément les pratiques professionnelles et pose de nouvelles questions éthiques et organisationnelles." },

  // ============================================================
  // CHAPITRE 3 — NUMÉRIQUE ET INNOVATION
  // ============================================================
  // SLIDE 43
  { type: 'section', title: "Chapitre 3", subtitle: "Numérique et innovation", description: "Transformation numérique, impact sur la relation d'aide, télémédecine et éthique du numérique." },

  // SLIDE 44
  { type: 'content', title: "Introduction — Chapitre 3",
    body: "Le numérique constitue un facteur majeur de transformation dans le secteur social et médico-social. La transformation numérique peut être définie comme l'intégration des technologies numériques dans l'ensemble des activités d'une organisation afin d'améliorer son fonctionnement et la qualité des services.\n\nDans le secteur social, cette transformation s'exprime à travers :",
    bullets: [
      "La dématérialisation des procédures administratives",
      "Le développement des dossiers usagers informatisés (DUI)",
      "L'utilisation d'outils de coordination entre professionnels",
      "L'émergence de nouvelles formes d'accompagnement à distance"
    ],
    highlight: "Le responsable d'unité doit accompagner ces transformations tout en veillant à préserver la qualité de la relation humaine." },

  // SLIDE 45
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 3",
    body: "À l'issue de ce module, les participants seront capables de :",
    bullets: [
      "Comprendre les transformations numériques du secteur social et médico-social",
      "Analyser l'impact du numérique sur la relation d'aide et les pratiques",
      "Identifier les opportunités et les limites des outils numériques",
      "Comprendre les enjeux éthiques et juridiques liés au numérique (RGPD)",
      "Intégrer les innovations numériques dans l'organisation des services"
    ] },

  // SLIDE 46
  { type: 'grid', title: "Sommaire — Chapitre 3 : Numérique et innovation", items: [
    { title: "1. La transformation numérique", desc: "Outils numériques dans le secteur social, avantages et limites" },
    { title: "2. Impact sur la relation d'aide", desc: "Apports, risques de déshumanisation et fracture numérique" },
    { title: "3. La télémédecine", desc: "Téléconsultation, téléexpertise, télésurveillance et enjeux" },
    { title: "4. L'éthique du numérique", desc: "Protection des données (RGPD), consentement, usage responsable" }
  ]},

  // SLIDE 47
  { type: 'section', title: "1. La transformation numérique", subtitle: "du secteur social", description: "Les outils numériques et leur impact sur les organisations." },

  // SLIDE 48
  { type: 'grid', title: "Les principaux outils numériques du secteur social", items: [
    { title: "Dossier Usager Informatisé (DUI)", desc: "Centralise les informations d'accompagnement : données administratives, projets personnalisés, observations, comptes rendus. Améliore la traçabilité et la coordination." },
    { title: "Outils de communication", desc: "Messageries sécurisées, plateformes collaboratives, visioconférences. Facilitent la circulation de l'information et la coordination des équipes." },
    { title: "Plateformes de coordination", desc: "Coordonnent les interventions de plusieurs acteurs autour d'une même personne. Utilisées dans les parcours complexes et la coopération sanitaire-sociale." },
    { title: "Outils de pilotage", desc: "Tableaux de bord, indicateurs de suivi, logiciels de gestion. Facilitent le pilotage de l'activité et la prise de décision." }
  ]},

  // SLIDE 49
  { type: 'content', title: "Avantages et limites du numérique",
    body: "Le développement du numérique présente des avantages significatifs mais comporte aussi des risques qu'il convient de maîtriser.",
    bullets: [
      "Avantage : meilleure traçabilité des interventions et des décisions",
      "Avantage : partage d'informations plus rapide entre professionnels",
      "Avantage : coordination renforcée et continuité de l'accompagnement",
      "Risque : surcharge administrative liée à la saisie informatique",
      "Risque : dépendance aux outils numériques (pannes, obsolescence)",
      "Risque : problèmes de sécurité des données sensibles"
    ],
    highlight: "Le cadre doit veiller à un usage raisonné des outils numériques, au service de la qualité et non comme une fin en soi." },

  // SLIDE 50
  { type: 'section', title: "2. Impact du numérique", subtitle: "sur la relation d'aide", description: "Apports, risques et rôle du cadre dans l'équilibre numérique-humain." },

  // SLIDE 51
  { type: 'content', title: "Les apports du numérique dans la relation d'aide",
    body: "La relation d'aide est une relation professionnelle visant à accompagner une personne dans la résolution de ses difficultés. Elle repose sur l'écoute, la confiance et la compréhension.\n\nLe numérique peut soutenir cette relation de plusieurs manières :",
    bullets: [
      "Amélioration de l'accès à l'information pour les usagers (droits, services)",
      "Coordination renforcée entre professionnels (éviter les ruptures de parcours)",
      "Continuité de l'accompagnement quand les rencontres physiques sont difficiles",
      "Suivi plus précis des situations grâce aux outils de traçabilité"
    ] },

  // SLIDE 52
  { type: 'content', title: "Les risques : déshumanisation et fracture numérique",
    body: "Malgré ses avantages, le numérique peut avoir des effets négatifs sur la relation d'aide si son utilisation n'est pas maîtrisée.",
    bullets: [
      "Déshumanisation : l'utilisation excessive des outils peut réduire les temps d'échange direct",
      "La relation humaine reste l'élément central de l'accompagnement social",
      "Fracture numérique : toutes les personnes n'ont pas les mêmes compétences numériques",
      "Risque de créer de nouvelles formes d'exclusion pour les publics fragiles",
      "Perte du sens : quand l'outil remplace la réflexion professionnelle"
    ],
    highlight: "Le responsable d'unité doit maintenir l'équilibre entre outils numériques et qualité de la relation humaine, et accompagner les professionnels dans cette appropriation." },

  // SLIDE 53
  { type: 'section', title: "3. La télémédecine", subtitle: "Nouvelles formes de soins à distance", description: "Téléconsultation, téléexpertise, télésurveillance et enjeux." },

  // SLIDE 54
  { type: 'content', title: "La télémédecine : définition",
    body: "La télémédecine correspond à l'utilisation des technologies numériques pour réaliser des actes médicaux à distance. Elle permet aux professionnels de santé d'échanger des informations médicales ou de réaliser certaines consultations sans présence physique du patient.",
    highlight: "La télémédecine peut être particulièrement utile dans les territoires isolés, pour les personnes à mobilité réduite ou en institution." },

  // SLIDE 55
  { type: 'grid', title: "Les différentes formes de télémédecine", items: [
    { title: "Téléconsultation", desc: "Consultation médicale à distance par visioconférence. Le patient consulte un médecin sans se déplacer." },
    { title: "Téléexpertise", desc: "Un professionnel de santé sollicite l'avis d'un confrère à distance. Facilite la collaboration entre spécialistes." },
    { title: "Télésurveillance", desc: "Suivi à distance de l'état de santé d'un patient via des outils numériques (capteurs, objets connectés)." },
    { title: "Enjeux", desc: "Amélioration de l'accès aux soins, réduction des déplacements, meilleure coordination. Mais vigilance sur la relation soignant-soigné." }
  ]},

  // SLIDE 56
  { type: 'section', title: "4. L'éthique du numérique", subtitle: "Protection des données et usage responsable", description: "RGPD, consentement des usagers et responsabilité du cadre." },

  // SLIDE 57
  { type: 'content', title: "La protection des données personnelles (RGPD)",
    body: "Les établissements sociaux et médico-sociaux manipulent de nombreuses informations sensibles concernant les usagers. Ces données doivent être protégées conformément au Règlement Général sur la Protection des Données (RGPD).\n\nPrincipes du RGPD :",
    bullets: [
      "Confidentialité des informations : accès limité aux personnes autorisées",
      "Minimisation : collecter uniquement les données nécessaires",
      "Limitation de la conservation : supprimer les données devenues inutiles",
      "Sécurisation des systèmes informatiques (mots de passe, chiffrement, sauvegardes)",
      "Droit d'accès et de rectification des personnes concernées"
    ] },

  // SLIDE 58
  { type: 'content', title: "Consentement et usage responsable",
    body: "Les personnes accompagnées doivent être informées de l'utilisation de leurs données et pouvoir exercer leurs droits.\n\nLe cadre doit promouvoir un usage responsable du numérique :",
    bullets: [
      "Informer les usagers des finalités du traitement de leurs données",
      "Recueillir leur consentement éclairé (sauf exceptions légales)",
      "Sensibiliser les professionnels aux enjeux de la protection des données",
      "Former les équipes aux outils numériques et aux bonnes pratiques",
      "Mettre en place des procédures de sécurité adaptées"
    ] },

  // SLIDE 59
  { type: 'content', title: "Conclusion — Chapitre 3",
    body: "Le numérique constitue un levier important d'innovation dans le secteur social et médico-social. Il permet d'améliorer la coordination, la gestion des informations et de développer de nouvelles formes d'accompagnement.\n\nCependant, ces transformations doivent être accompagnées avec vigilance :",
    bullets: [
      "Préserver la qualité de la relation humaine au cœur de l'accompagnement",
      "Protéger rigoureusement les données personnelles (RGPD)",
      "Lutter contre la fracture numérique et l'exclusion",
      "Former les professionnels à un usage éthique et responsable"
    ],
    highlight: "Le responsable d'unité joue un rôle essentiel dans l'accompagnement de ces transformations numériques." },

  // ============================================================
  // SLIDES DE TRANSITION (60-64)
  // ============================================================
  // SLIDE 60
  { type: 'content', title: "Synthèse des chapitres 1 à 3",
    body: "Les trois premiers chapitres de ce bloc ont permis d'explorer les fondements transversaux du management dans le secteur social :",
    bullets: [
      "Chapitre 1 : L'éthique comme boussole des décisions professionnelles",
      "Chapitre 2 : Le cadre juridique comme garant des droits et des pratiques",
      "Chapitre 3 : Le numérique comme levier d'innovation à maîtriser"
    ],
    highlight: "Le dernier chapitre aborde la dimension personnelle du cadre : gestion du stress, prévention de l'épuisement et développement de la posture managériale." },

  // SLIDE 61
  { type: 'content', title: "L'importance de la santé du cadre",
    body: "Le secteur social et médico-social expose les professionnels à des situations émotionnellement intenses. Les cadres sont particulièrement concernés car ils cumulent responsabilités managériales et charge émotionnelle liée aux situations des usagers.\n\nPrendre soin de soi n'est pas un luxe mais une nécessité professionnelle :",
    bullets: [
      "Un cadre épuisé ne peut pas soutenir efficacement ses équipes",
      "Le stress chronique dégrade la qualité des décisions",
      "La prévention de l'épuisement est un enjeu collectif, pas seulement individuel",
      "Une posture managériale équilibrée bénéficie à l'ensemble de l'organisation"
    ] },

  // SLIDE 62
  { type: 'content', title: "Les risques psychosociaux dans le secteur social",
    body: "Le secteur social est identifié comme un secteur à risque en matière de RPS (Risques Psychosociaux). Ces risques résultent de la combinaison de facteurs organisationnels, relationnels et individuels.",
    bullets: [
      "Charge émotionnelle : confrontation quotidienne à la souffrance et à la vulnérabilité",
      "Charge de travail : multiplicité des tâches et des responsabilités",
      "Conflits de valeurs : tensions entre éthique, contraintes institutionnelles et réalité du terrain",
      "Manque de reconnaissance : sentiment que le travail accompli n'est pas suffisamment valorisé",
      "Isolement du cadre : position intermédiaire parfois difficile entre direction et équipes"
    ] },

  // SLIDE 63
  { type: 'content', title: "Le cadre réglementaire de la prévention des RPS",
    body: "L'employeur a une obligation légale de prévention des risques psychosociaux (Code du travail, article L.4121-1). Le responsable d'unité contribue à cette prévention au niveau de son service.",
    bullets: [
      "Document Unique d'Évaluation des Risques Professionnels (DUERP)",
      "Plan de prévention des risques psychosociaux",
      "Droit d'alerte et droit de retrait des salariés",
      "Rôle du CHSCT / CSE dans la prévention",
      "Obligation de résultat de l'employeur en matière de santé au travail"
    ] },

  // SLIDE 64
  { type: 'content', title: "La qualité de vie au travail (QVT)",
    body: "La Qualité de Vie au Travail (QVT), désormais QVCT (Qualité de Vie et des Conditions de Travail), est un concept qui intègre les conditions matérielles, organisationnelles et relationnelles du travail.",
    bullets: [
      "Organisation du travail : charge réaliste, clarté des rôles, autonomie",
      "Relations professionnelles : communication, soutien, reconnaissance",
      "Conditions matérielles : environnement de travail adapté et sécurisé",
      "Conciliation vie professionnelle / vie personnelle",
      "Sens du travail : sentiment d'utilité et de contribution"
    ],
    highlight: "Le responsable d'unité peut agir concrètement sur la QVCT au sein de son service, même avec des marges de manœuvre limitées." },

  // ============================================================
  // CHAPITRE 4 — DÉVELOPPEMENT PERSONNEL DU CADRE
  // ============================================================
  // SLIDE 65
  { type: 'section', title: "Chapitre 4", subtitle: "Développement personnel du cadre", description: "Stress professionnel, burn-out, prévention de l'épuisement et posture managériale." },

  // SLIDE 66
  { type: 'content', title: "Introduction — Chapitre 4",
    body: "Les fonctions d'encadrement dans le secteur social impliquent une forte responsabilité humaine et organisationnelle. Les cadres doivent gérer des équipes, accompagner des situations complexes et répondre aux exigences institutionnelles.\n\nLe développement personnel du cadre constitue un levier essentiel pour maintenir un équilibre professionnel, prévenir les risques psychosociaux et renforcer l'efficacité managériale.\n\nIl ne s'agit pas d'une démarche individuelle isolée mais d'un processus d'apprentissage continu." },

  // SLIDE 67
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 4",
    body: "À l'issue de ce module, les participants seront capables de :",
    bullets: [
      "Comprendre les mécanismes du stress professionnel et ses impacts",
      "Identifier les facteurs de risque d'épuisement professionnel",
      "Mettre en œuvre des stratégies de prévention du burn-out",
      "Développer une posture managériale équilibrée et adaptée",
      "Cultiver leurs compétences émotionnelles et relationnelles"
    ] },

  // SLIDE 68
  { type: 'grid', title: "Sommaire — Chapitre 4 : Développement personnel", items: [
    { title: "1. Le stress professionnel", desc: "Définition, sources de stress dans l'encadrement, effets sur la santé" },
    { title: "2. Le burn-out", desc: "Syndrome d'épuisement, 3 dimensions de Maslach, facteurs de risque" },
    { title: "3. La prévention", desc: "Stratégies organisationnelles, soutien professionnel, analyse des pratiques" },
    { title: "4. La posture managériale", desc: "Compétences relationnelles, équilibre autorité/soutien, intelligence émotionnelle" }
  ]},

  // SLIDE 69
  { type: 'section', title: "1. Le stress professionnel", subtitle: "Mécanismes et impacts", description: "Comprendre le stress pour mieux le prévenir et le gérer." },

  // SLIDE 70
  { type: 'content', title: "Définition du stress",
    body: "Le stress peut être défini comme une réaction physiologique et psychologique de l'organisme face à une situation perçue comme exigeante, menaçante ou difficile à gérer.\n\nLe concept a été étudié par le chercheur Hans Selye, qui a montré que l'organisme réagit par un ensemble de réponses biologiques et psychologiques.",
    bullets: [
      "Eustress (stress positif) : stimule l'adaptation et la performance, mobilise l'énergie",
      "Distress (stress négatif) : dépasse les capacités d'adaptation, provoque l'épuisement",
      "Le stress chronique est particulièrement dangereux pour la santé"
    ],
    highlight: "Ce n'est pas le stress en lui-même qui est problématique, mais son intensité, sa durée et l'absence de récupération." },

  // SLIDE 71
  { type: 'grid', title: "Sources de stress dans les fonctions d'encadrement", items: [
    { title: "Charge de travail", desc: "Multiplicité des tâches : gestion administrative, coordination des équipes, suivi des usagers. Accumulation de responsabilités." },
    { title: "Gestion des conflits", desc: "Tensions entre professionnels, désaccords avec les familles, conflits organisationnels. Nécessite des compétences relationnelles et émotionnelles." },
    { title: "Responsabilités managériales", desc: "Prise de décision, arbitrages entre besoins des usagers, contraintes institutionnelles et attentes des équipes." },
    { title: "Position intermédiaire", desc: "Le cadre est tiraillé entre les exigences de la direction et les réalités du terrain. Sentiment d'isolement possible." }
  ]},

  // SLIDE 72
  { type: 'content', title: "Les effets du stress chronique sur la santé",
    body: "Lorsque le stress devient chronique, il peut avoir des conséquences importantes sur la santé des professionnels :",
    bullets: [
      "Effets physiques : fatigue persistante, troubles du sommeil, tensions musculaires, maux de tête",
      "Effets psychologiques : anxiété, irritabilité, perte de motivation, difficultés de concentration",
      "Effets relationnels : retrait social, tensions avec les collègues, perte d'empathie",
      "Effets professionnels : baisse de performance, erreurs, absentéisme, désengagement"
    ],
    highlight: "La prévention du stress constitue un enjeu majeur de santé au travail et de qualité managériale." },

  // SLIDE 73
  { type: 'section', title: "2. Le burn-out", subtitle: "Syndrome d'épuisement professionnel", description: "Comprendre les mécanismes de l'épuisement pour mieux le prévenir." },

  // SLIDE 74
  { type: 'content', title: "Définition du burn-out",
    body: "Le burn-out, ou syndrome d'épuisement professionnel, correspond à un état d'épuisement physique, émotionnel et mental résultant d'un stress professionnel chronique.\n\nCe concept a été étudié par les chercheurs Christina Maslach et Michael Leiter, qui ont identifié trois dimensions principales.",
    highlight: "Le burn-out n'est pas une faiblesse individuelle mais le résultat d'une exposition prolongée à des conditions de travail dégradées. C'est un signal d'alerte organisationnel." },

  // SLIDE 75
  { type: 'grid', title: "Les trois dimensions du burn-out (modèle de Maslach)", items: [
    { title: "1. Épuisement émotionnel", desc: "Fatigue intense et sentiment de surcharge émotionnelle. La personne n'a plus les ressources pour faire face. Sensation de « vide intérieur »." },
    { title: "2. Dépersonnalisation", desc: "Attitude de distance, de cynisme ou de détachement vis-à-vis des usagers et des collègues. Mécanisme de défense face à l'épuisement." },
    { title: "3. Diminution de l'accomplissement", desc: "Impression de ne plus être efficace, de ne plus apporter de valeur. Perte de sens et de motivation dans le travail. Auto-dévalorisation." }
  ]},

  // SLIDE 76
  { type: 'content', title: "Facteurs de risque dans le secteur social",
    body: "Plusieurs facteurs spécifiques au secteur social peuvent favoriser l'apparition du burn-out :",
    bullets: [
      "Confrontation répétée à la souffrance des usagers (charge émotionnelle cumulative)",
      "Pression institutionnelle (résultats, reporting, contraintes budgétaires)",
      "Manque de reconnaissance professionnelle (invisibilité du travail social)",
      "Sentiment d'impuissance face à des situations sans solution immédiate",
      "Isolement du cadre dans sa position intermédiaire",
      "Charge de travail excessive sans possibilité de délégation"
    ],
    highlight: "Les cadres doivent être attentifs à ces facteurs, tant pour eux-mêmes que pour les membres de leur équipe." },

  // SLIDE 77
  { type: 'section', title: "3. La prévention", subtitle: "de l'épuisement professionnel", description: "Stratégies organisationnelles, soutien entre pairs et développement des compétences." },

  // SLIDE 78
  { type: 'content', title: "Les stratégies organisationnelles de prévention",
    body: "La prévention du burn-out repose en grande partie sur l'organisation du travail. Le cadre peut agir concrètement sur plusieurs leviers :",
    bullets: [
      "Répartition équilibrée des tâches entre les membres de l'équipe",
      "Clarification des rôles et des responsabilités de chacun",
      "Régulation de la charge de travail (priorisation, délégation)",
      "Aménagement des espaces et des temps de travail",
      "Création de marges de manœuvre et d'autonomie pour les professionnels"
    ] },

  // SLIDE 79
  { type: 'content', title: "Le soutien professionnel",
    body: "Le soutien entre professionnels constitue un facteur de protection majeur contre l'épuisement.\n\nCe soutien peut prendre différentes formes :",
    bullets: [
      "Échanges informels entre collègues (moments de partage, décompression)",
      "Réunions d'équipe régulières avec espace de parole sur les difficultés",
      "Accompagnement managérial bienveillant et accessible",
      "Supervision individuelle ou collective par un intervenant extérieur",
      "Tutorat et parrainage pour les professionnels moins expérimentés"
    ],
    highlight: "Un climat de travail coopératif et bienveillant renforce la résilience collective de l'équipe." },

  // SLIDE 80
  { type: 'content', title: "L'analyse des pratiques professionnelles",
    body: "Les groupes d'analyse des pratiques professionnelles (APP) permettent aux professionnels de réfléchir collectivement à leurs expériences de travail, dans un cadre sécurisé et confidentiel.",
    bullets: [
      "Prise de recul sur les situations vécues au quotidien",
      "Partage d'expériences et de stratégies entre pairs",
      "Recherche collective de solutions aux difficultés rencontrées",
      "Diminution du sentiment d'isolement face aux situations complexes",
      "Prévention de l'usure professionnelle et du burn-out"
    ],
    highlight: "Les APP constituent un outil essentiel de prévention des risques psychosociaux et de développement professionnel continu." },

  // SLIDE 81
  { type: 'content', title: "Le développement des compétences émotionnelles",
    body: "Les compétences émotionnelles (intelligence émotionnelle) permettent au cadre de mieux gérer ses propres émotions et celles de ses interlocuteurs.\n\nCes compétences incluent notamment :",
    bullets: [
      "La conscience de soi : identifier et comprendre ses propres émotions",
      "La régulation émotionnelle : gérer ses réactions face aux situations stressantes",
      "L'empathie : comprendre les émotions et les besoins des autres",
      "Les compétences sociales : communiquer efficacement, résoudre les conflits",
      "La motivation intrinsèque : maintenir son engagement malgré les difficultés"
    ] },

  // SLIDE 82
  { type: 'section', title: "4. La posture managériale", subtitle: "Équilibre, compétences et leadership", description: "Construire une posture de cadre efficace et bienveillante." },

  // SLIDE 83
  { type: 'content', title: "Définition de la posture managériale",
    body: "La posture managériale peut être définie comme l'ensemble des attitudes, des comportements et des pratiques qu'un cadre adopte dans l'exercice de ses responsabilités.\n\nElle reflète la manière dont le cadre exerce son autorité, accompagne les équipes et prend des décisions.\n\nUne posture efficace n'est ni rigide ni figée : elle s'adapte aux situations, aux personnes et au contexte." },

  // SLIDE 84
  { type: 'grid', title: "Les compétences relationnelles du cadre", items: [
    { title: "L'écoute active", desc: "Accorder une attention réelle à l'interlocuteur : reformulation, prise en compte des émotions, absence de jugement." },
    { title: "La communication", desc: "Transmettre des informations claires, clarifier les objectifs, prévenir les malentendus. Outil central du management." },
    { title: "La prise de décision", desc: "Analyser les situations, prendre en compte les différents points de vue, assumer les choix dans des contextes parfois incertains." },
    { title: "La gestion des conflits", desc: "Identifier les tensions, faciliter le dialogue, rechercher des solutions équilibrées. Transformer les conflits en opportunités." }
  ]},

  // SLIDE 85
  { type: 'content', title: "L'équilibre entre autorité et soutien",
    body: "La posture managériale repose sur un équilibre entre l'exercice de l'autorité et le soutien des professionnels.\n\nCet équilibre est essentiel pour un management efficace :",
    bullets: [
      "Un management uniquement autoritaire génère des tensions, de la résistance et du désengagement",
      "Un management trop permissif crée un manque de repères, d'insécurité et de confusion",
      "La posture idéale est à la fois structurante (cadre clair, exigences) et bienveillante (écoute, soutien)",
      "L'autorité ne s'oppose pas à la bienveillance : elle en est le complément nécessaire"
    ],
    highlight: "Le cadre efficace sait être ferme sur les objectifs et souple sur les moyens, exigeant sur la qualité et soutenant face aux difficultés." },

  // SLIDE 86
  { type: 'content', title: "Conclusion — Chapitre 4",
    body: "Le développement personnel constitue une dimension essentielle de la fonction de cadre dans le secteur social et médico-social.\n\nLa compréhension des mécanismes du stress et de l'épuisement permet :",
    bullets: [
      "De préserver la santé physique et mentale des professionnels",
      "D'améliorer la qualité du management et des décisions",
      "De renforcer la cohésion et la résilience des équipes",
      "De créer un environnement favorable au bien-être et à la qualité de l'accompagnement"
    ],
    highlight: "En développant une posture managériale équilibrée, le responsable d'unité contribue à créer un environnement de travail favorable au bien-être des professionnels et à la qualité de l'accompagnement des usagers." },

  // ============================================================
  // CONCLUSION GÉNÉRALE
  // ============================================================
  // SLIDE 87
  { type: 'content', title: "Conclusion générale du Bloc 5",
    body: "Le Bloc de compétences 5 du CAFERUIS a permis d'explorer les dimensions transversales essentielles du management dans le secteur social et médico-social.\n\nCes quatre chapitres forment un ensemble cohérent :",
    bullets: [
      "L'éthique fournit la boussole des décisions professionnelles",
      "Le cadre juridique garantit la sécurité des pratiques et le respect des droits",
      "Le numérique offre des leviers d'innovation à maîtriser avec vigilance",
      "Le développement personnel renforce la posture et la résilience du cadre"
    ],
    highlight: "Ces compétences transversales constituent le socle d'un management responsable, durable et adapté aux transformations du secteur." },

  // SLIDE 88
  { type: 'grid', title: "Compétences clés du Bloc 5 — Synthèse", items: [
    { title: "Éthique et déontologie", desc: "Analyser les dilemmes éthiques, soutenir la réflexion collective, maîtriser le secret professionnel et partagé." },
    { title: "Cadre juridique", desc: "Connaître les lois fondatrices (2002-2, 2005, ASV, HPST), comprendre l'organisation administrative et anticiper les réformes." },
    { title: "Numérique et innovation", desc: "Accompagner la transformation numérique, maîtriser le RGPD, préserver l'équilibre humain-numérique." },
    { title: "Développement personnel", desc: "Prévenir le burn-out, développer l'intelligence émotionnelle, construire une posture managériale équilibrée." }
  ]},

  // SLIDE 89 — FÉLICITATIONS
  { type: 'congratulations', title: "Bloc 5 terminé !", message: "Félicitations ! Vous avez terminé le Bloc de compétences 5 — Formation transversale et complémentaire.\n\nVous maîtrisez désormais les dimensions éthiques, juridiques, numériques et personnelles du management en secteur social et médico-social.\n\nAvec ce dernier bloc, vous avez complété l'ensemble du programme CAFERUIS.\n\nBonne continuation dans votre parcours de validation des acquis de l'expérience !" }
  ];

  const totalSlides = slides.length;

  const handleNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      const next = currentSlide + 1;
      setCurrentSlide(next);
      if (onSlideChange) onSlideChange(next);
    } else {
      if (onComplete) onComplete();
    }
  }, [currentSlide, totalSlides, onComplete, onSlideChange]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      const prev = currentSlide - 1;
      setCurrentSlide(prev);
      if (onSlideChange) onSlideChange(prev);
    }
  }, [currentSlide, onSlideChange]);

  const goToSlide = useCallback((idx) => {
    setCurrentSlide(idx);
    if (onSlideChange) onSlideChange(idx);
  }, [onSlideChange]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); handleNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleNext, handlePrev]);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', textAlign: 'center', padding: '2rem' }}>
            <div style={{ background: primaryColor, color: 'white', padding: '1rem 2.5rem', borderRadius: '1rem', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '2rem' }}>
              {slide.icon} — CAFERUIS
            </div>
            <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: secondaryColor, marginBottom: '1rem', lineHeight: 1.2 }}>
              {slide.title}
            </h1>
            <p style={{ fontSize: '1.35rem', color: '#1a2e35', maxWidth: '700px', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              {slide.subtitle}
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1rem' }}>
              {slide.chapters.split('•').map((c, i) => (
                <span key={i} style={{ background: 'rgba(220,38,38,0.1)', color: primaryColor, padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: 600 }}>
                  {c.trim()}
                </span>
              ))}
            </div>
          </div>
        );

      case 'sommaire':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: secondaryColor, marginBottom: '2rem', textAlign: 'center' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', width: '100%', maxWidth: '700px' }}>
              {slide.chapters.map((ch, i) => (
                <button key={i} onClick={() => goToSlide(ch.targetSlide)}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: '0.75rem', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.12)'; e.currentTarget.style.borderColor = primaryColor; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.06)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.15)'; }}
                >
                  <span style={{ background: primaryColor, color: 'white', borderRadius: '50%', width: '2.2rem', height: '2.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>{ch.num}</span>
                  <span style={{ fontSize: '0.95rem', color: '#1a2e35', fontWeight: 500, lineHeight: 1.4 }}>{ch.title}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '65vh', textAlign: 'center', padding: '2rem',
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            borderRadius: '1.5rem', margin: '1rem', color: 'white'
          }}>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '1.3rem', fontWeight: 500, opacity: 0.95, maxWidth: '600px', marginBottom: '1rem' }}>{slide.subtitle}</p>
            {slide.description && <p style={{ fontSize: '1rem', opacity: 0.85, maxWidth: '550px', lineHeight: 1.6 }}>{slide.description}</p>}
          </div>
        );

      case 'content':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem 3rem', maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: secondaryColor, marginBottom: '1.5rem', textAlign: 'center', lineHeight: 1.3 }}>{slide.title}</h2>
            {slide.body && <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.7, marginBottom: '1rem', textAlign: 'justify', whiteSpace: 'pre-line' }}>{slide.body}</p>}
            {slide.bullets && (
              <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                {slide.bullets.map((b, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>
                    <span style={{ color: primaryColor, fontWeight: 700, flexShrink: 0 }}>&#9654;</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div style={{ marginTop: '1rem', padding: '1rem 1.25rem', background: 'rgba(220,38,38,0.06)', borderLeft: `4px solid ${primaryColor}`, borderRadius: '0.5rem', fontSize: '0.9rem', color: '#1a2e35', lineHeight: 1.6, fontStyle: 'italic' }}>
                {slide.highlight}
              </div>
            )}
          </div>
        );

      case 'grid':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: secondaryColor, marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: slide.items.length <= 3 ? 'repeat(auto-fit, minmax(250px, 1fr))' : 'repeat(2, 1fr)', gap: '1rem', width: '100%', maxWidth: '800px' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ padding: '1.25rem', background: 'white', borderRadius: '0.75rem', border: '1px solid rgba(220,38,38,0.12)', boxShadow: '0 2px 8px rgba(220,38,38,0.05)' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: primaryColor, marginBottom: '0.4rem' }}>{item.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#4B5563', lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>&#127942;</div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: primaryColor, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '1rem', color: '#374151', maxWidth: '600px', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{slide.message}</p>
          </div>
        );

      default:
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Slide non reconnue</div>;
    }
  };

  const slide = slides[currentSlide];

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: bgGradient, fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4px', background: 'rgba(220,38,38,0.12)', zIndex: 50 }}>
        <div style={{ height: '100%', background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, width: `${((currentSlide + 1) / totalSlides) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%' }}>{renderSlide(slide)}</div>
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,241,242,0.95)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(220,38,38,0.08)' }}>
        <button data-testid="bc5-prev-btn" onClick={handlePrev} disabled={currentSlide === 0}
          style={{ padding: '0.6rem 1.5rem', borderRadius: '0.5rem', border: `1px solid ${primaryColor}`, background: 'transparent', color: primaryColor, cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', opacity: currentSlide === 0 ? 0.4 : 1, fontWeight: 600, fontSize: '0.9rem' }}>
          &#8592; Précédent
        </button>
        <span style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 500 }}>{currentSlide + 1} / {totalSlides}</span>
        <button data-testid="bc5-next-btn" onClick={handleNext}
          style={{ padding: '0.6rem 1.5rem', borderRadius: '0.5rem', border: 'none', background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          {currentSlide === totalSlides - 1 ? 'Terminer' : 'Suivant &#8594;'}
        </button>
      </div>
    </div>
  );
};

export default CAFBC5Presentation;
